'use client';

import { useEffect } from 'react';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { UseOdoscopeCookieFromEpsFragmentFragmentDoc } from '@packages/gql/generated/shopping/UseOdoscopeCookieFromEpsFragmentFragmentDoc';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { ConsentCategory } from '@packages/shared/src/utils/getCookieConsent/getCookieConsent';

/* GraphQL */ `
  fragment UseOdoscopeCookieFromEpsFragment on EpsQueryResult {
    ... on SearchResultBase {
      odoscope {
        oscCookie
        trackingGroup
        trackingId
      }
    }
  }
`;

/** Cookie expiry in days */
const OSC_COOKIE_EXPIRY_DAYS = 365;

/**
 * Hook to process the Odoscope response from EPS and set the osc cookie.
 * The oscCookie value is base64 encoded and should be set as-is.
 *
 * @see https://learn.odoscope.cloud/knowledge-base/odoscopes-answer/
 */
export const useOdoscopeCookieFromEps = (
  maskedData?: FragmentType<typeof UseOdoscopeCookieFromEpsFragmentFragmentDoc>,
) => {
  const { setCookie } = useCookies();
  const { search } = useConfig();
  const hasMarketingConsent = useCookieConsent(ConsentCategory.Marketing);

  const data = maskedData
    ? unmask<typeof UseOdoscopeCookieFromEpsFragmentFragmentDoc>(maskedData)
    : undefined;

  const odoscopeToken = search?.odoscopeToken;

  useEffect(() => {
    // Skip if consent not given, or token not configured
    if (!hasMarketingConsent || !odoscopeToken) {
      return;
    }

    const odoscope = data && 'odoscope' in data ? data.odoscope : undefined;

    // Skip if no odoscope data in response
    if (!odoscope?.oscCookie) {
      return;
    }

    const cookieName = `osc-${odoscopeToken}`;
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + OSC_COOKIE_EXPIRY_DAYS);

    setCookie(null, cookieName, odoscope.oscCookie, {
      expires: expiry,
      path: '/',
      sameSite: 'lax',
    });
  }, [data, hasMarketingConsent, odoscopeToken, setCookie]);
};
